import React, { useEffect, useMemo, useState } from "react";
import { openPopup } from "../Common/popupUIStore";

import StudentItem from "../Common/StudentItem";
import IconButton from "../Common/IconButton";
import UIIcon from "../Common/UIIcon";
import SearchToggle from "../Common/SearchToggle";

const escapeRegExp = (s = "") =>
   String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const highlightText = (text, query, highlightClassName) => {
   const t = String(text ?? "");
   const q = String(query ?? "");
   if (!q) return t;

   const safe = escapeRegExp(q);
   const parts = t.split(new RegExp(`(${safe})`, "gi"));

   return parts.map((part, index) =>
      part.toLowerCase() === q.toLowerCase() ? (
         <i key={index} className={highlightClassName}>
            {part}
         </i>
      ) : (
         part
      ),
   );
};

const firstLetter = (v) =>
   String(v || "")
      .trim()
      .charAt(0) || "";

function getInitials(student) {
   const fn = String(student?.firstName || "").trim();
   const ln = String(student?.lastName || "").trim();

   const a = firstLetter(fn);
   const b = firstLetter(ln);
   if (a && b) return (a + b).toUpperCase();

   const two = fn.slice(0, 2);
   if (two) return two.toUpperCase();

   return "–";
}

function hashStringToUInt(str) {
   let h = 0;
   for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
   return h >>> 0;
}

const AVATAR_HUES = [
   { h: 70, s: 75 },
   { h: 0, s: 100 },
   { h: 30, s: 100 },
   { h: 54, s: 95 },
   { h: 130, s: 65 },
   { h: 210, s: 90 },
   { h: 255, s: 98 },
   { h: 285, s: 100 },
   { h: 330, s: 96 },
];

const AVATAR_LIGHTNESSES = [94, 92, 90, 88, 86, 84, 82, 80, 78, 76, 74];

const AVATAR_COLORS = AVATAR_HUES.flatMap(({ h, s }) =>
   AVATAR_LIGHTNESSES.map((l) => `hsl(${h} ${s}% ${l}%)`),
);

function getRandomAvatarColor() {
   return AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
}

function getAvatarColorFromName(student) {
   const fullName =
      `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
   const hasLetter = /\p{L}/u.test(fullName);
   if (!hasLetter) return null;

   const normalized = fullName.normalize("NFKD");
   const idx = hashStringToUInt(normalized) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

function resolveProfessorLabel(group) {
   const direct =
      group?.professorName ||
      group?.professor?.fullName ||
      group?.professor?.name ||
      null;
   if (direct) return String(direct);

   const first = String(group?.professor?.firstName || "").trim();
   const last = String(group?.professor?.lastName || "").trim();
   const full = `${first} ${last}`.trim();
   if (full) return full;

   return "Profesor";
}

function isProtectedToken(token) {
   return (
      String(token ?? "")
         .trim()
         .toUpperCase() === "ABCD1234"
   );
}

export default function ProfessorGroupsReadonly({
   groups = [],
   students = [],
   status = "idle",
   title = "Grupele mele",
}) {
   const [search, setSearch] = useState(() => ({
      open:
         typeof window !== "undefined" &&
         window.matchMedia("(min-width: 769px)").matches,
      query: "",
   }));
   const [viewMode, setViewMode] = useState({ mode: "list", group: null });

   useEffect(() => {
      if (typeof window === "undefined") return undefined;

      const mq = window.matchMedia("(min-width: 769px)");
      const syncDesktopSearch = (event) => {
         if (!event.matches) return;
         setSearch((prev) => (prev.open ? prev : { ...prev, open: true }));
      };

      syncDesktopSearch(mq);

      if (mq.addEventListener) mq.addEventListener("change", syncDesktopSearch);
      else mq.addListener(syncDesktopSearch);

      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", syncDesktopSearch);
         else mq.removeListener(syncDesktopSearch);
      };
   }, []);

   const visibleGroups = useMemo(
      () => (Array.isArray(groups) ? groups : []).filter((g) => !isProtectedToken(g?.token)),
      [groups],
   );

   const groupedUsersByGroup = useMemo(
      () =>
         visibleGroups.map((group) => ({
            ...group,
            members: (Array.isArray(students) ? students : []).filter(
               (u) => String(u?.groupId) === String(group?.id),
            ),
         })),
      [students, visibleGroups],
   );

   const filteredGroups = useMemo(() => {
      const q = (search.query || "").toLowerCase().trim();
      if (!q) return groupedUsersByGroup;

      return groupedUsersByGroup.filter(
         (g) =>
            String(g?.name || "")
               .toLowerCase()
               .includes(q) ||
            String(g?.token || "")
               .toLowerCase()
               .includes(q) ||
            resolveProfessorLabel(g)
               .toLowerCase()
               .includes(q),
      );
   }, [groupedUsersByGroup, search.query]);

   const detailGroup =
      viewMode.mode === "details" && viewMode.group
         ? groupedUsersByGroup.find(
              (group) => String(group?.id) === String(viewMode.group?.id),
           ) || viewMode.group
         : null;

   const detailMembers = useMemo(() => detailGroup?.members ?? [], [detailGroup]);

   const filteredDetailMembers = useMemo(() => {
      const q = String(search.query || "")
         .trim()
         .toLowerCase();

      if (!q) return detailMembers;

      return detailMembers.filter((student) =>
         [
            student?.firstName,
            student?.lastName,
            student?.email,
            student?.phone,
            student?.idnp,
         ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(q),
      );
   }, [detailMembers, search.query]);

   useEffect(() => {
      if (viewMode.mode !== "details" || !viewMode.group) return;
      const stillVisible = visibleGroups.some(
         (g) => String(g?.id) === String(viewMode.group?.id),
      );
      if (!stillVisible) setViewMode({ mode: "list", group: null });
   }, [viewMode, visibleGroups]);

   const detailColorByKey = useMemo(() => {
      const m = new Map();
      detailMembers.forEach((student, idx) => {
         const key = String(
            student?.id ?? student?.phone ?? student?.email ?? `__idx_${idx}`,
         );
         const deterministic = getAvatarColorFromName(student);
         m.set(key, deterministic || getRandomAvatarColor());
      });
      return m;
   }, [detailMembers]);

   const handleToggleSearch = () => {
      setSearch((prev) =>
         prev.open ? { open: false, query: "" } : { ...prev, open: true },
      );
   };

   const infoHint =
      status === "loading"
         ? "Se incarca grupele..."
         : status === "error"
           ? "Nu am putut incarca grupele profesorului."
           : status === "none"
             ? "Nu ai grupe asociate."
             : null;

   return (
      <div className="studentsGroupsUI">
         <div
            className={`studentsGroupsUI__header ${search.open ? "is-open" : ""}`}
         >
            <h2 className="studentsGroupsUI__title">{title}</h2>

            <div className="studentsGroupsUI__right">
               <SearchToggle
                  open={search.open}
                  value={search.query}
                  onValueChange={(val) =>
                     setSearch((prev) => ({ ...prev, query: val }))
                  }
                  onToggle={handleToggleSearch}
                  placeholder={
                     viewMode.mode === "details"
                        ? "Cauta student in grupa curenta..."
                        : "Cauta grupa..."
                  }
                  wrapperClassName="studentsGroupsUI__search"
                  inputClassName="studentsGroupsUI__inputSearch"
                  buttonClassName="studentsGroupsUI__iconBtn"
                  iconClassName={`studentsGroupsUI__icon ${search.open ? "is-rotated" : ""}`}
                  titleOpen="Inchide cautarea"
                  titleClosed="Cauta"
               />
            </div>
         </div>

         <div className="studentsGroupsUI__gridWrap">
            <div className="studentsGroupsUI__grid">
               {infoHint && viewMode.mode === "list" && (
                  <p className="studentsGroupsUI__empty">{infoHint}</p>
               )}

               {viewMode.mode === "list" &&
                  filteredGroups.map((group) => {
                     const membersCount = Number.isFinite(
                        Number(group?.studentCount),
                     )
                        ? Number(group.studentCount)
                        : group?.members?.length || 0;

                     const professorLabel = resolveProfessorLabel(group);

                     return (
                        <div key={group?.id} className="studentsGroupsUI__item">
                           <div className="studentsGroupsUI__itemLeft">
                              <div className="studentsGroupsUI__itemTop">
                                 <h3>
                                    {highlightText(
                                       group?.name || `Grupa #${group?.id}`,
                                       search.query,
                                       "studentsGroupsUI__highlight",
                                    )}
                                 </h3>
                              </div>

                              <div className="studentsGroupsUI__itemBottom">
                                 <span className="studentsGroupsUI__itemKey">
                                    <UIIcon
                                       name="key"
                                       className="studentsGroupsUI__keyIcon"
                                    />
                                    {highlightText(
                                       group?.token || "",
                                       search.query,
                                       "studentsGroupsUI__highlight",
                                    )}
                                 </span>
                              </div>

                              <div className="studentsGroupsUI__itemBottom">
                                 <span className="studentsGroupsUI__itemProfessor">
                                    {highlightText(
                                       professorLabel,
                                       search.query,
                                       "studentsGroupsUI__highlight",
                                    )}
                                 </span>
                              </div>

                              <p className="studentsGroupsUI__count">
                                 {membersCount} pers
                              </p>
                           </div>

                           <div className="studentsGroupsUI__itemRight">
                              <IconButton
                                 className="studentsGroupsUI__itemIcon"
                                 icon="eye"
                                 iconClassName="studentsGroupsUI__icon"
                                 onClick={() => {
                                    setSearch((prev) => ({
                                       ...prev,
                                       query: "",
                                    }));
                                    setViewMode({ mode: "details", group });
                                 }}
                                 title="Vezi membri"
                              />
                           </div>
                        </div>
                     );
                  })}

               {viewMode.mode === "list" &&
                  status === "ok" &&
                  filteredGroups.length === 0 && (
                     <p className="studentsGroupsUI__empty">
                        Nu exista grupe care sa se potriveasca filtrului.
                     </p>
                  )}

               {viewMode.mode === "details" && viewMode.group && (
                  <>
                     <button
                        type="button"
                        className="studentsGroupsUI__backBtn"
                        onClick={() => {
                           setSearch((prev) => ({ ...prev, query: "" }));
                           setViewMode({ mode: "list", group: null });
                        }}
                     >
                        Inapoi la grupe
                     </button>

                     {filteredDetailMembers.length > 0 ? (
                        filteredDetailMembers.map((student, idx) => {
                           const key = String(
                              student?.id ??
                                 student?.phone ??
                                 student?.email ??
                                 `__idx_${idx}`,
                           );
                           const color =
                              detailColorByKey.get(key) ||
                              getRandomAvatarColor();

                           return (
                              <StudentItem
                                 key={student?.id ?? key}
                                 student={student}
                                 color={color}
                                 initials={getInitials(student)}
                                 onOpen={(selected) =>
                                    openPopup("instructorStudents", {
                                       initialStudent: selected,
                                    })
                                 }
                                 highlightQuery={search.query}
                                 secondaryText={student?.phone || "–"}
                              />
                           );
                        })
                     ) : (
                        <p className="studentsGroupsUI__empty">
                           {search.query.trim()
                              ? "Nu exista studenti care sa se potriveasca."
                              : "Nu sunt studenti in aceasta grupa."}
                        </p>
                     )}
                  </>
               )}
            </div>
         </div>
      </div>
   );
}
